// F3: スキーマ検証（対象ゲームの npm run validate:maps 実行 + ajv 単体検証）
// 検証ロジックは各ゲームの scripts/validate-maps.mjs / schemas/*.json をそのまま使う（二重実装しない）。
import { existsSync, readdirSync } from 'node:fs';
import path from 'node:path';
import { createRequire } from 'node:module';
import { requireGameDir, updateGameMeta } from './games.mjs';
import { readJson } from './json-utils.mjs';
import { runNpm } from './proc.mjs';
import { auditLog } from './logger.mjs';
import { SEVENFOLD_DIR, TOOLS_DIR } from './repo-paths.mjs';

/** npm run validate:maps を実行し、結果サマリを返す。 */
export async function runValidateMaps(gameId, { onLine = () => {}, signal = null } = {}) {
    const dir = requireGameDir(gameId);
    const r = await runNpm(dir, ['run', 'validate:maps'], { onLine, signal });
    const problems = r.output
        .split(/\r?\n/)
        .filter((l) => /error|エラー|invalid|failed|✗|✖|NG\b/i.test(l) && !/0 error/i.test(l));
    const result = { ok: r.code === 0, code: r.code, problems, output: r.output };
    updateGameMeta(gameId, (meta) => ({
        ...meta,
        lastValidation: { ...(meta.lastValidation ?? {}), schema: { ok: result.ok, at: new Date().toISOString() } },
    }));
    auditLog('validate-schema', { gameId, ok: result.ok });
    return result;
}

/**
 * ajv 解決順: 対象ゲーム → tools 自身 → sevenfold。
 * CI は sevenfold / tools のみ npm ci するため、ゲーム未 ci でも単体検証できるようにする。
 */
function resolveAjv(gameDir) {
    for (const dir of [gameDir, TOOLS_DIR, SEVENFOLD_DIR]) {
        const pkg = path.join(dir, 'package.json');
        if (!existsSync(pkg)) continue;
        try {
            const req = createRequire(pkg);
            const Ajv = req('ajv');
            return Ajv.default ?? Ajv;
        } catch {
            // 次候補へ
        }
    }
    return null;
}

/** maps 配下の相対パスからスキーマファイルを推定する。 */
// maps/_shared/ 配下のスキーマ対応表（エンジン scripts/validate-maps.mjs の SHARED_FILE_SCHEMAS と同一）。
// _shared/enemies.json（敵マスタ）はマップ配置用 enemies.schema.json とファイル名が衝突するが
// 構造が異なり、エンジン側も意図的に検証対象外＝basename 一致で誤検証しない
const SHARED_FILE_SCHEMAS = {
    'characters.json': 'characters.schema.json',
    'items.json': 'items.schema.json',
    'skills.json': 'skills.schema.json',
    'shop-master.json': 'shop-master.schema.json',
    'equipment.json': 'equipment.schema.json',
    'loot-tables.json': 'loot-tables.schema.json',
};

// scripts/validate-maps.mjs と同じ座標・角度の整数ルール。ブラウザ版は npm を
// 起動できないため、単体プリフライトとVFS全件検証の双方で適用する。
const INTEGER_RULE_MAP_FILES = new Set([
    'meta.json', 'terrain.json', 'objects.json', 'npcs.json', 'enemies.json', 'events.json', 'treasures.json',
]);
const INTEGER_COORD_ARRAY_KEYS = new Set([
    'pos', 'posOffset', 'spawnPos', 'respawnPos', 'entrancePos', 'center', 'offset', 'start', 'end',
    'position', 'targetPos', 'warpPos', 'xRange', 'yRange', 'zRange',
]);
const INTEGER_COORD_SCALAR_KEYS = new Set([
    'x', 'y', 'z', 'minX', 'maxX', 'minY', 'maxY', 'minZ', 'maxZ', 'facingAngle', 'yaw', 'pitch',
    'roll', 'faceYawOffsetDeg', 'rotationOffsetDeg', 'angle',
]);

export function collectIntegerRuleViolations(data, fileName) {
    if (!INTEGER_RULE_MAP_FILES.has(fileName)) return [];
    const violations = [];
    const walk = (node, jsonPath, key = null, parentKey = null) => {
        if (Array.isArray(node)) {
            const arrayParent = key ?? parentKey;
            if (INTEGER_COORD_ARRAY_KEYS.has(arrayParent)) {
                node.forEach((value, index) => {
                    if (typeof value === 'number' && Number.isFinite(value) && !Number.isInteger(value)) {
                        violations.push(`${jsonPath}[${index}] = ${value}`);
                    }
                });
            }
            node.forEach((value, index) => walk(value, `${jsonPath}[${index}]`, null, arrayParent));
            return;
        }
        if (!node || typeof node !== 'object') return;
        for (const [childKey, value] of Object.entries(node)) {
            const childPath = `${jsonPath}.${childKey}`;
            if (typeof value === 'number' && INTEGER_COORD_SCALAR_KEYS.has(childKey)
                && Number.isFinite(value) && !Number.isInteger(value)) {
                violations.push(`${childPath} = ${value}`);
            }
            walk(value, childPath, childKey, key ?? parentKey);
        }
    };
    walk(data, '$');
    return violations;
}

export function schemaFileFor(gameDir, mapsRelPath) {
    const base = path.basename(mapsRelPath);
    if (!base.endsWith('.json')) return null;
    const candidates = [];
    // blueprints/*.json は blueprint.schema.json
    if (mapsRelPath.includes('blueprints/')) {
        candidates.push('blueprint.schema.json');
    } else if (/(^|\/)_shared\//.test(mapsRelPath)) {
        const shared = SHARED_FILE_SCHEMAS[base];
        if (!shared) return null;
        candidates.push(shared);
    } else {
        candidates.push(`${base.slice(0, -'.json'.length)}.schema.json`);
    }
    for (const c of candidates) {
        const p = path.join(gameDir, 'schemas', c);
        if (existsSync(p)) return p;
    }
    return null;
}

/**
 * 単一 JSON の即時検証（F2 取り込みプリフライト用）。
 * ajv 未インストール（npm ci 前）の場合は skipped=true でパース確認のみ。
 */
export function validateSingleJson(gameId, mapsRelPath, jsonValue) {
    const dir = requireGameDir(gameId);
    const schemaPath = schemaFileFor(dir, mapsRelPath);
    if (!schemaPath) {
        return { ok: true, skipped: true, reason: '対応するスキーマ無し（validate:maps の対象外ファイル）' };
    }
    const Ajv = resolveAjv(dir);
    if (!Ajv) {
        return { ok: true, skipped: true, reason: 'ajv 未インストール（npm ci 未実行）。配置後に validate:maps で検証してください' };
    }
    const ajv = new Ajv({ allErrors: true, strict: false });
    const schema = readJson(schemaPath);
    const validate = ajv.compile(schema);
    const schemaOk = validate(jsonValue);
    const integerViolations = schemaOk
        ? collectIntegerRuleViolations(jsonValue, path.basename(mapsRelPath))
        : [];
    const ok = schemaOk && integerViolations.length === 0;
    return {
        ok,
        skipped: false,
        schema: path.basename(schemaPath),
        errors: [
            ...(validate.errors ?? []).map((e) => `${e.instancePath || '(root)'} ${e.message}`),
            ...integerViolations.slice(0, 40).map((entry) => `座標・角度は整数で指定してください: ${entry}`),
        ],
    };
}

/** ゲーム内の maps JSON 一覧（相対パス）。 */
export function listMapsJsonFiles(gameId) {
    const dir = requireGameDir(gameId);
    const mapsDir = path.join(dir, 'maps');
    const files = [];
    const walk = (d) => {
        for (const entry of readdirSync(d, { withFileTypes: true })) {
            const child = path.join(d, entry.name);
            if (entry.isDirectory()) walk(child);
            else if (entry.name.endsWith('.json')) {
                files.push(path.relative(dir, child).split(path.sep).join('/'));
            }
        }
    };
    if (existsSync(mapsDir)) walk(mapsDir);
    return files;
}

/** ブラウザVFS向け: npmを使わず、同じスキーマ単体検証を maps 配下のJSON全件へ適用する。 */
export function runValidateMapsInProcess(gameId, { onLine = () => {} } = {}) {
    const dir = requireGameDir(gameId);
    const files = listMapsJsonFiles(gameId);
    const problems = [];
    let passedFiles = 0;
    let failedFiles = 0;
    let skippedFiles = 0;
    for (const relPath of files) {
        try {
            const result = validateSingleJson(gameId, relPath, readJson(path.join(dir, relPath)));
            if (result.skipped) {
                skippedFiles++;
                continue;
            }
            if (result.ok) {
                passedFiles++;
            } else {
                failedFiles++;
                problems.push(`${relPath}: ${result.errors.join(' / ')}`);
            }
        } catch (e) {
            failedFiles++;
            problems.push(`${relPath}: JSONを検証できません: ${e.message}`);
        }
    }
    const result = {
        ok: failedFiles === 0,
        code: failedFiles === 0 ? 0 : 1,
        totalFiles: files.length,
        passedFiles,
        failedFiles,
        skippedFiles,
        problems,
        output: problems.join('\n'),
    };
    updateGameMeta(gameId, (meta) => ({
        ...meta,
        lastValidation: { ...(meta.lastValidation ?? {}), schema: { ok: result.ok, at: new Date().toISOString() } },
    }));
    auditLog('validate-schema-in-process', { gameId, ok: result.ok, total: files.length, failed: failedFiles });
    onLine(`schema: ${result.ok ? 'OK' : 'NG'}（成功 ${passedFiles} / 失敗 ${failedFiles} / 対象外 ${skippedFiles}）`);
    for (const problem of problems) onLine(`  ✖ ${problem}`);
    return result;
}
